/** @format */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import type { ParentGroupSummary } from "@question-bank/shared";
import { buildGroupEntry, replaceEntry, type PaperEntry } from "../../utils/paper";
import { CartContext } from "./useCart";

const PAPER_STORAGE_KEY = "addedQuestions.v2";

export const CartProvider = ({ children }: { children: ReactNode }) => {
	const [addedQuestions, setAddedQuestions] = useState<PaperEntry[]>(() => {
		const stored = localStorage.getItem(PAPER_STORAGE_KEY);
		return stored ? JSON.parse(stored) : [];
	});

	useEffect(() => {
		localStorage.setItem(PAPER_STORAGE_KEY, JSON.stringify(addedQuestions));
	}, [addedQuestions]);

	const addWholeQuestion = useCallback((question: ParentGroupSummary) => {
		setAddedQuestions((prev) => replaceEntry(prev, buildGroupEntry(question)));
	}, []);

	const addParts = useCallback((entry: PaperEntry) => {
		setAddedQuestions((prev) => replaceEntry(prev, entry));
	}, []);

	const removeQuestion = useCallback((entry: PaperEntry) => {
		setAddedQuestions((prev) => prev.filter((q) => q.parentQuestionId !== entry.parentQuestionId));
	}, []);

	const clearAll = useCallback(() => setAddedQuestions([]), []);

	const entryFor = useCallback(
		(parentQuestionId: string) => addedQuestions.find((q) => q.parentQuestionId === parentQuestionId),
		[addedQuestions],
	);

	const reorderQuestions = useCallback((from: number, to: number) => {
		setAddedQuestions((prev) => {
			const newList = [...prev];
			const [draggedQuestion] = newList.splice(from, 1);
			newList.splice(to, 0, draggedQuestion);
			return newList;
		});
	}, []);

	const value = useMemo(
		() => ({ addedQuestions, addWholeQuestion, addParts, removeQuestion, clearAll, entryFor, reorderQuestions }),
		[addedQuestions, addWholeQuestion, addParts, removeQuestion, clearAll, entryFor, reorderQuestions],
	);
	return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
